import React, { useEffect, useState } from "react";
import Loader from "@/components/loader";
import { commonLoaderStyle, LOADER_TYPES } from "@/constants";
import { useTranslation } from "react-i18next";
import SendOTP from "./sendOtpCopy";

type Props = {
  otpToken: string;
  email: string;
  onEditEmail: () => void;
  onSwitchToMobile: () => void;
  loading?: boolean;
  sendOtpCount: number;
  expiresAtMs?: number | null;
  requestEmailOtp?: () => Promise<number>;
  verifyEmailOtp?: (
    code: string
  ) => Promise<{ success: boolean; message?: string }>;
  initialErrorMessage?: string;
};

const loaderStyle = {
  ...commonLoaderStyle,
  backgroundColor: "inherit"
};

export const EmailOtpPanel: React.FC<Props> = ({
  otpToken,
  email,
  onEditEmail,
  onSwitchToMobile,
  loading = false,
  sendOtpCount,
  expiresAtMs = null,
  requestEmailOtp,
  verifyEmailOtp,
  initialErrorMessage
}) => {
  const { t } = useTranslation("auth");

  const [otpCode, setOtpCode] = useState("");
  const [otpValid, setOtpValid] = useState(true);
  const [otpErrorMessage, setOtpErrorMessage] = useState("");
  const [showVerifyButtonLoader, setShowVerifyButtonLoader] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (initialErrorMessage) {
      setOtpValid(false);
      setOtpErrorMessage(initialErrorMessage);
    }
  }, [initialErrorMessage]);

  const resend = async () => {
    if (!requestEmailOtp || resending) return;
    setResending(true);
    try {
      await requestEmailOtp();
      setOtpErrorMessage("");
      setOtpValid(true);
    } catch (e: any) {
      setOtpErrorMessage(
        e?.response?.data?.message || e?.message || "Failed to resend OTP"
      );
      setOtpValid(false);
    } finally {
      setResending(false);
    }
  };

  const onVerify = async () => {
    if (!verifyEmailOtp) return;
    setShowVerifyButtonLoader(true);
    try {
      const resp = await verifyEmailOtp(otpCode);
      if (resp?.success) {
        setOtpValid(true);
        setOtpErrorMessage("");
      } else {
        setOtpValid(false);
        setOtpErrorMessage(resp?.message || "Invalid code");
      }
    } finally {
      setShowVerifyButtonLoader(false);
    }
  };

  if (loading) {
    return (
      <div className="panel" aria-busy="true">
        <span className="panel_title">{t("enterOtp")}</span>
        <div className="panel_loader_slot position-relative">
          <Loader
            type={LOADER_TYPES.DOTS}
            visible
            style={loaderStyle}
            size={10}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <span className="panel_title">{t("enterOtp")}</span>
      <p className="panel_subtitle">
        {t("otpSentToEmail")} <span>{email}</span>{" "}
        <button type="button" className="link" onClick={onEditEmail}>
          {t("edit")}
        </button>
      </p>

      <div className="panel_sendotp_wrap">
        <SendOTP
          disableBtn={!otpToken}
          phone={email}
          otp={otpCode}
          handleOTPChange={setOtpCode}
          verifyOtp={onVerify}
          otpValid={otpValid}
          sendOtp={resend}
          sendOtpCount={sendOtpCount}
          errorMessage={otpErrorMessage}
          showLoader={showVerifyButtonLoader}
          expiresAtMs={expiresAtMs}
          showMobileVerification={true}
          onSwitchToMobile={onSwitchToMobile}
          resending={resending}
          showCaptcha={false}
        />
      </div>
    </div>
  );
};
